import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import { Eye as EyeIcon } from '@phosphor-icons/react/dist/ssr/Eye';
import { EyeSlash as EyeSlashIcon } from '@phosphor-icons/react/dist/ssr/EyeSlash';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { paths } from '@/paths';
import { RouterLink } from '@/components/core/v2/link';
import { LogoWithBranding } from '@/components/core/v2/logo';
import axios from '@/lib/v2/axios';
import { authClient } from '@/lib/v2/auth/client';
import { useUser } from '@/hooks/v2/use-user';

interface InvitationApiResponse {
  error?: string | null;
  message?: string;
  verified: boolean;
  needs_password?: boolean;
  email?: string;
  community_name?: string;
  can_resend?: boolean;
}

const setPasswordSchema = zod
  .object({
    password: zod.string().min(8, { message: 'Password must be at least 8 characters' }),
    password_confirmation: zod.string().min(1, { message: 'Please confirm your password' }),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type SetPasswordValues = zod.infer<typeof setPasswordSchema>;

export default function Invitation(): React.JSX.Element {
  const { token } = useParams();
  const navigate = useNavigate();
  const [resendSuccess, setResendSuccess] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery<{ data: InvitationApiResponse }>({
    queryKey: ['invitation', token],
    queryFn: async () => await axios.post(`/api/v2/invitations/verify`, { token }),
    retry: false,
  });
  const invitationData = data?.data;

  const resendMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/api/v2/invitations/resend-by-token`, { token });
      return response.data;
    },
    onSuccess: () => {
      setResendSuccess(true);
    },
  });

  const { checkSession } = useUser();
  const [activateError, setActivateError] = useState<string | null>(null);

  const activateMutation = useMutation({
    mutationFn: async (values: SetPasswordValues) => {
      const { error } = await authClient.activateWithToken({
        token: token!,
        password: values.password,
        password_confirmation: values.password_confirmation,
      });
      if (error) throw new Error(error);
    },
    onSuccess: async () => {
      await checkSession?.();
      navigate(paths.home, { replace: true });
    },
    onError: (err: Error) => {
      setActivateError(err.message);
    },
  });

  // Redirect to sign-in only when verified and does NOT need password (legacy flow)
  useEffect(() => {
    if (invitationData?.verified && !invitationData?.needs_password) {
      navigate(paths.auth.signIn, { replace: true });
    }
  }, [invitationData, navigate]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error">Unable to verify invitation</Alert>
      </Box>
    );
  }

  if (invitationData?.error) {
    const isExpired = invitationData.error === 'expired';
    const canResend = invitationData.can_resend;
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {invitationData.message}
        </Alert>
        
        {resendSuccess ? <Alert severity="success" sx={{ mb: 2 }}>
            A new invitation has been sent to your email. Please check your inbox for the new invitation link.
          </Alert> : null}
        
        {isExpired && canResend && !resendSuccess ? <Box sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
              Would you like us to send you a new invitation?
            </Typography>
            <Button
              disabled={resendMutation.isPending}
              onClick={() => { resendMutation.mutate(); }}
              variant="contained"
            >
              {resendMutation.isPending ? 'Sending...' : 'Send New Invitation'}
            </Button>
          </Box> : null}
        
        {invitationData.error === 'already_accepted' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
              You can now sign in to your account.
            </Typography>
            <Button
              onClick={() => { navigate(paths.auth.signIn); }}
              variant="contained"
            >
              Go to Sign In
            </Button>
          </Box>
        )}
        
        {invitationData.error === 'not_found' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">
              Please check your invitation link or contact your administrator.
            </Typography>
          </Box>
        )}
        
        {resendMutation.isError ? <Alert severity="error" sx={{ mt: 2 }}>
            Failed to send new invitation. Please try again or contact support.
          </Alert> : null}
      </Box>
    );
  }

  // Verified and needs password - show set-password form
  if (invitationData?.verified && invitationData?.needs_password) {
    return (
      <SetPasswordForm
        email={invitationData.email}
        communityName={invitationData.community_name}
        onSubmit={activateMutation.mutate}
        isPending={activateMutation.isPending}
        error={activateError}
      />
    );
  }

  // If invitation is verified and no password needed, we're redirecting (effect above).
  if (invitationData?.verified) {
    return <></>;
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}
    >
      <Alert severity="success">Account Verified</Alert>
    </Box>
  );
}

function SetPasswordForm({
  email,
  communityName,
  onSubmit,
  isPending,
  error,
}: {
  email?: string;
  communityName?: string;
  onSubmit: (values: SetPasswordValues) => void;
  isPending: boolean;
  error: string | null;
}): React.JSX.Element {
  const [showPassword, setShowPassword] = useState(false);
  const { control, handleSubmit, formState: { errors } } = useForm<SetPasswordValues>({
    defaultValues: { password: '', password_confirmation: '' },
    resolver: zodResolver(setPasswordSchema),
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        px: 2,
      }}
    >
      <Stack spacing={3} sx={{ maxWidth: 400, width: '100%' }}>
        <Box component={RouterLink} href={paths.home} sx={{ display: 'inline-block', textDecoration: 'none', color: 'inherit' }}>
          <LogoWithBranding logoHeight={80} logoWidth={80} />
        </Box>
        <Typography variant="h5">Set your password</Typography>
        <Typography color="text.secondary" variant="body2">
          Welcome! Create a password to activate your account{communityName ? ` and join ${communityName}` : ''}.
          {email ? ` You're signing in as ${email}.` : ''}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <form onSubmit={handleSubmit((values) => onSubmit(values))}>
          <Stack spacing={2}>
            <Controller
              control={control}
              name="password"
              render={({ field }) => (
                <FormControl error={Boolean(errors.password)}>
                  <InputLabel>Password</InputLabel>
                  <OutlinedInput
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    endAdornment={
                      showPassword ? (
                        <EyeIcon
                          cursor="pointer"
                          fontSize="var(--icon-fontSize-md)"
                          onClick={() => setShowPassword(false)}
                        />
                      ) : (
                        <EyeSlashIcon
                          cursor="pointer"
                          fontSize="var(--icon-fontSize-md)"
                          onClick={() => setShowPassword(true)}
                        />
                      )
                    }
                    label="Password"
                    placeholder="At least 8 characters"
                  />
                  {errors.password ? (
                    <FormHelperText>{errors.password.message}</FormHelperText>
                  ) : null}
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="password_confirmation"
              render={({ field }) => (
                <FormControl error={Boolean(errors.password_confirmation)}>
                  <InputLabel>Confirm password</InputLabel>
                  <OutlinedInput {...field} type="password" label="Confirm password" placeholder="Re-enter password" />
                  {errors.password_confirmation ? (
                    <FormHelperText>{errors.password_confirmation.message}</FormHelperText>
                  ) : null}
                </FormControl>
              )}
            />
            <LoadingButton
              disabled={isPending}
              loading={isPending}
              loadingPosition="center"
              type="submit"
              variant="contained"
              fullWidth
            >
              Activate account
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Box>
  );
}
